import { useLocation as useUserLocation } from "@/context/LocationContext";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ArrowRight, Shield } from "lucide-react";
import { api } from "@/lib/api";

const VERTICAL_LABELS = {
  parking: "🅿️ Parking",  equipment_rental: "🛠️ Tool Rental",
  cleaning: "✨ Cleaning", pest_control: "🐛 Pest Control",
  laundry: "🧺 Laundry",   water_ro: "💧 RO Water",
  vending_machine: "🥤 Vending", saas_micro: "💻 Micro-SaaS",
};

/**
 * Nearby services widget for Home / Apps pages.
 * Uses browser geolocation (with graceful fallback) → GET /services/nearby.
 */
export default function NearbyServicesWidget() {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [locErr, setLocErr]   = useState(null);

  // Reuse the app's already-resolved location (same one the Home header + weather show). No second GPS
  // prompt, and no "everywhere" fallback that used to surface Mumbai/Delhi providers.
  const { loc } = useUserLocation();
  // The context's placeholder (Mumbai, source="default") is not the user's location.
  const resolved = !!loc && loc.source !== "default";
  const hasGeo = resolved && typeof loc.lat === "number" && typeof loc.lng === "number";
  useEffect(() => {
    if (!hasGeo && !(resolved && loc?.city)) { setLocErr("Set your location to see nearby providers"); setLoading(false); return; }
    let live = true;
    setLoading(true);
    setLocErr(null);
    const params = hasGeo ? { lat: loc.lat, lng: loc.lng, per_vertical: 3 } : { city: loc.city, per_vertical: 3 };
    api.get("/services/nearby", { params })
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData({ verticals: {} }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [hasGeo, resolved, loc?.lat, loc?.lng, loc?.city]);

  if (loading) return <div className="p-6 text-center"><Loader2 className="inline animate-spin text-stone-400" size={20}/></div>;
  if (!data && !locErr) return null;

  const nonEmpty = Object.entries(data?.verticals || {}).filter(([_, rows]) => rows?.length > 0);
  if (nonEmpty.length === 0) return (
    <div className="rounded-3xl bg-white border border-stone-200 p-4 text-center text-sm text-stone-500" data-testid="nearby-services-empty">
      {locErr || `No providers within ${data?.radius_km || 25} km of ${loc?.city || "you"} yet.`} Be the first to <Link to="/apps" className="text-fuchsia-700 font-black hover:underline">list a service</Link>.
    </div>
  );

  return (
    <section className="space-y-2" data-testid="nearby-services-widget">
      <div className="flex items-center gap-2">
        <MapPin size={13} className="text-fuchsia-600"/>
        <h3 className="font-black text-stone-900">Near you</h3>
        {locErr && <span className="text-[10px] text-stone-400">· {locErr}</span>}
      </div>
      <div className="space-y-3">
        {nonEmpty.slice(0, 5).map(([v, rows]) => (
          <div key={v}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] uppercase tracking-widest font-black text-stone-600">{VERTICAL_LABELS[v] || v}</div>
              <Link to={`/services/${v}`} className="text-[11px] font-black text-fuchsia-700 hover:underline inline-flex items-center gap-1" data-testid={`nearby-see-all-${v}`}>
                See all <ArrowRight size={10}/>
              </Link>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {rows.map(p => (
                <Link key={p.provider_id} to={`/services/${v}`} className="min-w-[180px] shrink-0 rounded-2xl bg-white border border-stone-200 p-3 hover:border-stone-400" data-testid={`nearby-card-${p.provider_id}`}>
                  <div className="flex items-start justify-between gap-1">
                    <div className="font-black text-sm text-stone-900 truncate">{p.name}</div>
                    {p.verified && <Shield size={12} className="text-emerald-600 shrink-0" title="Verified"/>}
                  </div>
                  <div className="text-[10px] text-stone-500 mt-0.5">
                    {p.city || "Anywhere"}{p.distance_km != null && ` · ${p.distance_km} km`}
                  </div>
                  <div className="text-xs font-black text-emerald-700 mt-1">₹{p.price_inr}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
